import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check for existing session on mount AND handle OAuth2 callback
  useEffect(() => {
    handleOAuth2Callback();
  }, []);

  // Handle Twitter OAuth2 callback (when returning from Twitter auth)
  const handleOAuth2Callback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuth2Callback = urlParams.get('twitter_oauth2_callback');
    const tokenId = urlParams.get('token_id');
    
    if (isOAuth2Callback === 'true' && tokenId) {
      console.log('OAuth2 callback detected, exchanging token...');
      
      try {
        // Exchange the temporary token for a session
        const response = await fetch(`${API_BASE_URL}/api/auth/twitter/exchange-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ token_id: tokenId })
        });
        
        if (response.ok) {
          const userData = await response.json();
          console.log('OAuth2 token exchange successful:', userData);
          
          // Include beta approval status in user object
          const userWithBeta = {
            ...(userData.user || userData),
            is_beta_approved: userData.is_beta_approved ?? (userData.user?.is_beta_approved ?? false)
          };
          setUser(userWithBeta);
          
          // Auto-detect and save timezone
          if (userData?.user?.id || userData?.id) {
            detectAndSaveTimezone(userData.user?.id || userData.id);
            recordTermsAgreement(userData.user?.id || userData.id);
          }
          
          // Clean up URL (remove OAuth params)
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          console.error('OAuth2 token exchange failed:', await response.text());
          // Fall back to regular auth check
          await checkAuth();
        }
      } catch (error) {
        console.error('OAuth2 callback error:', error);
        await checkAuth();
      } finally {
        setLoading(false);
      }
    } else {
      // No OAuth2 callback, do regular auth check
      await checkAuth();
    }
  };

  const checkAuth = async () => {
    try {
      // First check if we're in demo mode (persisted in localStorage)
      const isDemoMode = localStorage.getItem('demo_mode') === 'true';
      if (isDemoMode) {
        const demoUser = {
          id: 'demo_user',
          email: 'demo@example.com',
          name: 'Demo User',
          picture: null,
          x_username: null
        };
        setUser(demoUser);
        setLoading(false);
        
        // Auto-detect and save timezone for demo user too
        detectAndSaveTimezone('demo_user');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include' // Include cookies
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Auto-detect and save timezone when user authenticates
        if (userData?.id) {
          detectAndSaveTimezone(userData.id);
          
          // Record terms agreement if it was captured during login
          recordTermsAgreement(userData.id);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Detect browser timezone and save to user preferences
  const detectAndSaveTimezone = async (userId) => {
    try {
      // Get browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Check if we already saved timezone for this session
      const savedTzKey = `timezone_saved_${userId}`;
      if (sessionStorage.getItem(savedTzKey)) {
        return; // Already saved this session
      }
      
      console.log(`Detected browser timezone: ${browserTimezone}`);
      
      // Send to backend to update email preferences
      const response = await fetch(`${API_BASE_URL}/api/email-preferences/${userId}/timezone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ timezone: browserTimezone })
      });
      
      if (response.ok) {
        console.log(`Timezone ${browserTimezone} saved for user ${userId}`);
        sessionStorage.setItem(savedTzKey, 'true');
      }
    } catch (error) {
      console.error('Failed to save timezone:', error);
    }
  };

  // Record terms agreement after successful authentication
  const recordTermsAgreement = async (userId) => {
    try {
      // Check if terms were agreed to during this login session
      const agreedAt = localStorage.getItem('terms_agreed_at');
      if (!agreedAt) {
        console.log('No terms agreement found in localStorage');
        return;
      }
      
      // Check if we already recorded this agreement
      const recordedKey = `terms_recorded_${userId}`;
      if (sessionStorage.getItem(recordedKey)) {
        return; // Already recorded this session
      }
      
      console.log(`Recording terms agreement for user ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/legal/record-terms-agreement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          agreed_at: agreedAt,
          terms_version: '2026-01-29'
        })
      });
      
      if (response.ok) {
        console.log('Terms agreement recorded successfully');
        sessionStorage.setItem(recordedKey, 'true');
        // Clean up localStorage after successful recording
        localStorage.removeItem('terms_agreed_at');
      } else {
        console.error('Failed to record terms agreement:', await response.text());
      }
    } catch (error) {
      console.error('Error recording terms agreement:', error);
    }
  };

  const loginWithGoogle = async () => {
    try {
      console.log('Initiating Google login...');
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_BASE_URL}/api/auth/google/login?t=${timestamp}`, {
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Google login response:', data);
      
      if (data.auth_url) {
        console.log('Redirecting to Google OAuth...');
        // Force full page redirect with no cache
        window.location.replace(data.auth_url);
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Google login failed:', error);
      alert('Failed to initiate Google login: ' + error.message);
    }
  };

  const loginWithTwitter = async () => {
    try {
      console.log('Initiating Twitter login...');
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_BASE_URL}/api/auth/twitter/login?t=${timestamp}`, {
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Twitter login response:', data);
      
      if (data.auth_url) {
        console.log('Redirecting to Twitter OAuth...');
        // Force full page redirect with no cache (same as Google)
        window.location.replace(data.auth_url);
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Twitter login failed:', error);
      alert('Failed to initiate Twitter login: ' + error.message);
    }
  };

  const loginDemo = () => {
    // Set demo user
    const demoUser = {
      id: 'demo_user',
      email: 'demo@example.com',
      name: 'Demo User',
      picture: null,
      x_username: null
    };
    setUser(demoUser);
    localStorage.setItem('demo_mode', 'true');
    // Navigate to app after setting user
    window.location.href = '/app';
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      localStorage.removeItem('demo_mode');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginWithTwitter,
    loginDemo,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
